import type { ReactNode } from 'react'

import type { IconName } from '@/components/icons/icons'
import { ICONS } from '@/components/icons/icons'

import styles from './AuthShell.module.css'

interface AuthShellProps {
  title: string
  /** Optional status icon above the title (no-access, errors, loading). */
  icon?: IconName
  tone?: 'accent' | 'bad'
  children: ReactNode
}

const YEAR = new Date().getFullYear()

/**
 * Two-panel auth chrome shared by the sign-in, callback, loading and no-access screens — the same
 * layout as LOS and POS: the RBI brand panel (navy, shared logo) on the left, the screen's content
 * on the right. On narrow screens the brand panel collapses to a header strip.
 */
export function AuthShell({ title, icon, tone = 'accent', children }: AuthShellProps) {
  const Icon = icon ? ICONS[icon] : null
  return (
    <main className={styles.shell}>
      <div className={styles.card}>
        <aside className={styles.brand}>
          <div className={styles.texture} aria-hidden="true" />
          <img className={styles.logo} src="/brand/rbi-logo.png" alt="RBI Private Lending" width={57} height={40} />
          <div className={styles.brandMid}>
            <p className={styles.eyebrow}>RBI Private Lending · FP&amp;A</p>
            <p className={styles.headline}>
              Department runway, <span className={styles.highlight}>one view</span> of spend vs. budget
            </p>
            <p className={styles.sub}>
              Operating expense actuals against the quarter&rsquo;s budget for every department, synced from Sage
              Intacct.
            </p>
          </div>
          <ul className={styles.facts}>
            <li>
              <strong>Quarter</strong>
              <span>to date, by month</span>
            </li>
            <li>
              <strong>Intacct</strong>
              <span>synced weekdays</span>
            </li>
          </ul>
        </aside>

        <section className={styles.panel} aria-labelledby="auth-title">
          <div className={styles.body}>
            {Icon ? (
              <span className={`${styles.icon} ${tone === 'bad' ? styles.iconBad : ''}`}>
                <Icon size={20} />
              </span>
            ) : null}
            <h1 className={styles.title} id="auth-title">
              {title}
            </h1>
            {children}
          </div>
          <p className={styles.copyright}>© {YEAR} RBI Private Lending</p>
        </section>
      </div>
    </main>
  )
}
